import { useQueries } from '@tanstack/react-query';
import { Check, Clock, Loader2, RefreshCw, X } from 'lucide-react';
import { drogaRozsylki } from '@/components/SiatkaEdycji';
import { Button } from '@/components/ui/button';
import { fetchStanOperacji, type StanOperacji } from '@/lib/baza-api';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  STAN NA WĘZŁACH PO WYSYŁCE — z siatki albo z naprawy „wg <baza>" (29.09.2026).
 *
 *  ⚠️⚠️ Z PLANU ETAPU 2 („edycja w siatce z podglądem, co pójdzie w dół, i stanem na węzłach").
 *  Dotąd ekran mówił tylko „zakolejkowano", a to, która baza odmówiła — np. wiersz zmieniony
 *  w SPSUI między cyklami agenta — widać było dopiero w zakładce „Zmiany".
 *
 *  ⚠️ ODPYTUJEMY, PÓKI OPERACJA TRWA, i ani chwili dłużej: agenci biorą zmiany w swoich cyklach
 *  (master pierwszy, maszyny po nim), a zakończona operacja już się nie zmieni.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Stany całej operacji, po których nic się już nie zmieni — ten sam podział, co w rachunku operacji. */
const KONCOWE = new Set(['CONFIRMED', 'FAILED', 'SUPERSEDED']);
const CO_MS = 3000;

type Wezel = StanOperacji['wezly'][number];

const WYGLAD: Record<Wezel['stan'], { ikona: typeof Check; klasa: string }> = {
  CZEKA: { ikona: Clock, klasa: 'border-border text-muted-foreground' },
  W_DRODZE: { ikona: Loader2, klasa: 'border-primary/40 text-primary' },
  ZROBIONE: { ikona: Check, klasa: 'border-[var(--success)]/50 text-[var(--success)]' },
  BLAD: { ikona: X, klasa: 'border-[var(--destructive)]/50 text-[var(--destructive)]' },
};

function ChipWezla({ krok, w }: { krok: number; w: Wezel }) {
  const { t } = useI18n();
  const { ikona: Ikona, klasa } = WYGLAD[w.stan];
  /** Pominięte wiersze i uwagi agenta w dymku — liczba „zrobione" bez nich ukrywałaby, że czegoś nie było. */
  const dymek = [
    w.pominietych > 0 ? t('baza.pominieto').replace('{n}', String(w.pominietych)) : null,
    w.uwagi,
    w.blad,
  ]
    .filter((x): x is string => x !== null && x !== '')
    .join(' — ');
  return (
    <span
      title={dymek === '' ? undefined : dymek}
      className={cn('inline-flex items-center gap-1 rounded border bg-background px-1.5 py-0.5 font-mono text-[11px]', klasa)}
    >
      <Ikona className={cn('size-3', w.stan === 'W_DRODZE' && 'animate-spin')} aria-hidden />
      {krok} · {w.nodeId}
      <span className="font-sans">{t(`baza.stanWezla.${w.stan}`)}</span>
    </span>
  );
}

export function StanWysylki({
  groupId,
  opIds,
  onOdswiez,
}: {
  groupId: string;
  opIds: readonly string[];
  onOdswiez: () => void;
}) {
  const { t } = useI18n();
  const stany = useQueries({
    queries: opIds.map((opId) => ({
      queryKey: ['baza', 'operacja', groupId, opId],
      queryFn: () => fetchStanOperacji(groupId, opId),
      refetchInterval: (q: { state: { data?: StanOperacji } }) =>
        q.state.data !== undefined && KONCOWE.has(q.state.data.status) ? false : CO_MS,
    })),
  });
  const gotowe = stany.length > 0 && stany.every((q) => q.data?.status === 'CONFIRMED');
  /**
   * ⚠️ PRZY HURCIE OPERACJI JEST DO DWUDZIESTU — licznik mówi od razu, ile jeszcze czeka, a lista
   * przewija się we własnym pudełku, żeby nie zepchnąć wyniku zapytania z ekranu.
   */
  const zrobione = stany.filter((q) => q.data?.status === 'CONFIRMED').length;

  return (
    <div
      className={cn(
        'space-y-1.5 rounded-md border p-2 text-xs',
        gotowe ? 'border-[var(--success)]/40 bg-[var(--success)]/10' : 'border-border bg-muted/30',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span>{gotowe ? t('baza.wyslanoGotowe') : t('baza.wyslano')}</span>
        {opIds.length > 1 && (
          <span className="text-muted-foreground">
            {t('baza.operacjiGotowe').replace('{z}', String(zrobione)).replace('{n}', String(opIds.length))}
          </span>
        )}
        <Button size="sm" variant="outline" onClick={onOdswiez}>
          <RefreshCw className="size-3.5" />
          {t('baza.odswiezWynik')}
        </Button>
      </div>
      <div className={cn('space-y-1.5', opIds.length > 3 && 'max-h-72 overflow-y-auto')}>
        {stany.map((q, i) => {
          const s = q.data;
          if (s === undefined) {
            return q.isError ? (
              <p key={opIds[i]} className="text-[var(--destructive)]">
                {(q.error as Error).message}
              </p>
            ) : (
              <Loader2 key={opIds[i]} className="size-3.5 animate-spin text-muted-foreground" />
            );
          }
          /** Kolejność i kroki jak w „Pójdzie:" przed wysyłką — master pierwszy, maszyny po nim. */
          const wgWezla = new Map(s.wezly.map((w) => [w.nodeId, w]));
          return (
            <div key={s.opId} className="space-y-1">
              <div className="flex flex-wrap items-center gap-1.5">
                {opIds.length > 1 && (
                  <span className="text-muted-foreground">{t('baza.operacjaNr').replace('{n}', String(i + 1))}</span>
                )}
                {drogaRozsylki(s.wezly).map((d) => (
                  <ChipWezla key={d.nodeId} krok={d.krok} w={wgWezla.get(d.nodeId)!} />
                ))}
              </div>
              {s.wezly
                .filter((w) => w.stan === 'BLAD')
                .map((w) => (
                  <p key={w.nodeId} className="text-[var(--destructive)]">
                    {w.nodeId}: {w.blad ?? t('common.error')}
                  </p>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
